import React from "react";
import Title from "../Components/Elements/Title";
import MainContainer from "../Components/Elements/MainContainer";
import Text from "../Components/Elements/Text";
import AdaptiveContainer from "../Components/Elements/AdaptiveContainer";
import { Helmet } from "react-helmet";
import { Formik } from "formik";
import * as Yup from 'yup';
import { ContactFormValues } from "../Models/ContactFormValues";
import { useStore } from "../Stores/store";
import { observer } from "mobx-react-lite";
import { Link } from "react-router-dom";

interface Props {
    useHelm?: boolean;
}


const Contact = observer(({ useHelm = false} : Props) => {
    const { generalStore } = useStore();
    const { contactFormLoading, postContact, contactFormVals } = generalStore;

    const validationSchema = Yup.object().shape({
        name: Yup.string().required('Name is required').max(50, 'Name is too long'),
        email: Yup.string().required('Email is required').email('Invalid email address'),
        question: Yup.string().required('Question is required')
    });

    const formVals : ContactFormValues = contactFormVals ?? {
        name: '',
        email: '',
        question: ''
    };

    const handleSubmit = async (values: ContactFormValues) => {
        await postContact(values);
    };

    return (
        <MainContainer>
            {
                useHelm && (
                    <Helmet>
                        <meta charSet="utf-8" />
                        <title>H&R Wedding | Contact</title>
                    </Helmet>
                )
            }
            
            <Title size="3">Contact Us</Title>
            <Text>If you have any questions or queries, then please do reach out.</Text>
            <AdaptiveContainer>
                <div className="grid grid-cols-1 md:grid-cols-2 md:col-span-2 gap-4 w-full">
                    <div>
                        <Text className="font-bold">Email: <a href="mailto:ryanholliewedding@outlook.com">ryanholliewedding@outlook.com</a></Text>
                    </div>
                    <div>
                        <Text className="font-bold">Phone: <a href="tel:07710585938">07710585938</a></Text>
                    </div>
                </div>
                <div className="col-span-2 space-y-4 w-full md:w-4/5 lg:w-4/5 sm:w-11/12">
                    <Text>Alternatively, feel free to reach out to us using the form below and we&apos;ll get back to you.</Text>
                    <Formik
                        initialValues={formVals}
                        onSubmit={(values) => handleSubmit(values)}
                        enableReinitialize
                        validationSchema={validationSchema}
                    >
                        {({handleSubmit, handleChange, values, errors, touched, isValid}) => (
                            <form onSubmit={handleSubmit}>
                                <AdaptiveContainer columns={1} className="py-2">
                                    {errors.name && touched.name && <div className="text-red-500">{errors.name}</div>}
                                    <label htmlFor="name" className="font-bold font-lora">Name<span className="text-red-600">*</span></label>
                                    <input
                                        type="text"
                                        name="name"
                                        id="name"
                                        onChange={handleChange}
                                        value={values.name}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                                    />
                                </AdaptiveContainer>
                                <AdaptiveContainer columns={1} className="py-2">
                                    {errors.email && touched.email && <div className="text-red-500">{errors.email}</div>}
                                    <label htmlFor="email" className="font-bold font-lora">Email<span className="text-red-600">*</span></label>
                                    <input
                                        type="email"
                                        name="email"
                                        id="email"
                                        onChange={handleChange}
                                        value={values.email}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                                    />
                                </AdaptiveContainer>
                                <AdaptiveContainer columns={1} className="py-2">
                                    {errors.question && touched.question && <div className="text-red-500">{errors.question}</div>}
                                    <label htmlFor="question" className="font-bold font-lora">Question<span className="text-red-600">*</span></label>
                                    <textarea
                                        name="question"
                                        id="question"
                                        onChange={handleChange}
                                        value={values.question}
                                        rows={4}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                                    />
                                </AdaptiveContainer>
                                <AdaptiveContainer columns={1} className="py-2">
                                    <input
                                        type="submit"
                                        disabled={contactFormLoading || !isValid}
                                        className={`inline-flex justify-center py-2 font font-lora px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${contactFormLoading ? 'bg-gray-400' : 'bg-blue-900 opacity-70 hover:bg-indigo-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                                        value={contactFormLoading ? 'Submitting...' : 'Submit'}
                                    />
                                </AdaptiveContainer>
                            </form>
                        )}
                    </Formik>
                </div>
            </AdaptiveContainer>
            { useHelm && <Text className="pt-4">
                <Link
                    to="/app"
                    className="bg-blue-500 hover:bg-blue-700 font font-lora text-white font-bold py-2 px-4 rounded my-2 inline-block text-center"
                >
                    Back to home
                </Link>
            </Text>}
        </MainContainer>
    );
});

export default Contact;